/**
 * React Hook für Push-Notifications
 * Verwaltet die Initialisierung und den Status von Push-Benachrichtigungen
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { pushNotificationService } from '../services/pushNotifications';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { getNotificationTarget, NotificationType } from '../types';

export interface UsePushNotificationsReturn {
  /** Ob Push-Notifications verfügbar sind (native Plattform) */
  isAvailable: boolean;
  /** Ob der Service initialisiert wurde */
  isInitialized: boolean;
  /** Ob gerade initialisiert wird */
  isInitializing: boolean;
  /** Aktueller Berechtigungsstatus */
  permissionStatus: 'granted' | 'denied' | 'prompt' | 'unknown';
  /** Aktueller Push-Token (falls verfügbar) */
  token: string | null;
  /** Fehler bei der Initialisierung */
  error: string | null;
  /** Manuell Berechtigungen anfordern */
  requestPermission: () => Promise<boolean>;
  /** Push-Notifications deaktivieren (z.B. bei Logout) */
  deactivate: () => Promise<void>;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<'granted' | 'denied' | 'prompt' | 'unknown'>('unknown');
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const initAttempted = useRef(false);

  const isAvailable = pushNotificationService.isAvailable();

  // Notification-Handler: Navigation bei Tap
  const handleNotificationAction = useCallback((notification: { title?: string; body?: string; data?: Record<string, unknown> }) => {
    const data = notification.data as {
      type?: NotificationType;
      target_url?: string;
      target_page?: string;
      notification_id?: string;
    } | undefined;

    if (!data) return;

    // Versuche Navigation basierend auf Notification-Daten
    let targetPath: string | null = null;

    if (data.target_url) {
      // Externe URL - öffne im Browser (nicht in-app)
      window.open(data.target_url, '_blank');
      return;
    }

    if (data.target_page) {
      // Interne Seite
      const routes: Record<string, string> = {
        dashboard: '/',
        ssw: '/ssw',
        tools: '/tools',
        chat: '/chat',
        profile: '/profile',
        calendar: '/calendar',
        partner: '/partner',
        help: '/help',
      };
      targetPath = routes[data.target_page] || '/';
    } else if (data.type) {
      // Basierend auf Notification-Type
      targetPath = getNotificationTarget({
        type: data.type,
        target_url: null,
        target_page: null,
      } as Parameters<typeof getNotificationTarget>[0]);
    }

    if (targetPath) {
      navigate(targetPath);
    }
  }, [navigate]);

  // Notification im Vordergrund empfangen
  const handleNotificationReceived = useCallback((notification: { title?: string; body?: string; data?: Record<string, unknown> }) => {
    // Hier könnte man ein In-App-Toast oder ähnliches anzeigen
    console.log('[usePushNotifications] Received in foreground:', notification.title);
  }, []);

  // Initialisierung bei Login
  useEffect(() => {
    if (!user?.id || !isAvailable || initAttempted.current) {
      return;
    }

    const initPush = async () => {
      setIsInitializing(true);
      setError(null);
      initAttempted.current = true;

      try {
        // Berechtigungsstatus prüfen
        const status = await pushNotificationService.checkPermissions();
        setPermissionStatus(status);

        // Initialisieren mit Handlers
        const success = await pushNotificationService.initialize({
          onNotificationReceived: handleNotificationReceived,
          onNotificationActionPerformed: handleNotificationAction,
          onRegistrationError: (err) => {
            console.error('[usePushNotifications] Registration error:', err);
            setError('Push-Registrierung fehlgeschlagen');
          },
        });

        if (success) {
          setIsInitialized(true);
          setToken(pushNotificationService.getToken());
          setPermissionStatus('granted');
        }
      } catch (err) {
        console.error('[usePushNotifications] Init error:', err);
        setError('Push-Initialisierung fehlgeschlagen');
      } finally {
        setIsInitializing(false);
      }
    };

    initPush();
  }, [user?.id, isAvailable, handleNotificationAction, handleNotificationReceived]);

  // Berechtigungen anfordern
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isAvailable) return false;

    try {
      const granted = await pushNotificationService.requestPermissions();
      setPermissionStatus(granted ? 'granted' : 'denied');

      if (granted && !isInitialized) {
        // Neu initialisieren nach Berechtigung
        const success = await pushNotificationService.initialize({
          onNotificationReceived: handleNotificationReceived,
          onNotificationActionPerformed: handleNotificationAction,
        });

        if (success) {
          setIsInitialized(true);
          setToken(pushNotificationService.getToken());
        }
      }

      return granted;
    } catch (err) {
      console.error('[usePushNotifications] Request permission error:', err);
      return false;
    }
  }, [isAvailable, isInitialized, handleNotificationAction, handleNotificationReceived]);

  // Deaktivieren bei Logout
  const deactivate = useCallback(async (): Promise<void> => {
    await pushNotificationService.deactivate();
    setToken(null);
    setIsInitialized(false);
    initAttempted.current = false;
  }, []);

  // Cleanup bei User-Wechsel
  useEffect(() => {
    if (!user?.id) {
      initAttempted.current = false;
    }
  }, [user?.id]);

  return {
    isAvailable,
    isInitialized,
    isInitializing,
    permissionStatus,
    token,
    error,
    requestPermission,
    deactivate,
  };
}
